<script setup>
import { ref, nextTick, onBeforeUnmount, onMounted } from 'vue';
import kebabCase from 'lodash-es/kebabCase';
import Gumshoe from 'gumshoejs';
import { useRouter, useRoute } from 'vue-router';

import { useLenis } from '@/components/scroll/Lenis.js';
import StickyBar from '@/components/sticky-bar/StickyBar.vue';
import CustomSelect from '@/components/custom-select/CustomSelect.vue';

const anchorLinks = ref({});
// const sections = ref([]);
const lenis = useLenis();
const spy = ref(null);
const overflowing = ref(false);
const router = useRouter();
const route = useRoute();
const activatedValue = ref(null);
const rootEl = ref(null);

const props = defineProps({
    colors: {
        type: Object,
        default: () => ({
            txt: 'dark-blue',
            bg: 'light-blue',
            ui: 'dark-blue',
            uiTxt: 'white',
        }),
    },
    anchors: {
        type: Array,
        default: () => [],
    },
    uniqueId: {
        type: String,
        default: '',
        required: true,
    },
});

const options = [];
for (let i = 0, il = props.anchors.length; i < il; ++i) {
    const anch = props.anchors[i];
    options.push({ id: kebabCase(anch), title: anch });
}
activatedValue.value = options[0];

const activateLink = (event) => {
    const slug = event.detail.link.getAttribute('href').replace('#', '');
    event.detail.link.classList.add('active', `bg-${props.colors.ui}`, `text-${props.colors.uiTxt}`);
    router.push({
        hash: event.detail.link.getAttribute('href'),
    });
    const theOption = options.filter((item) => item.id == slug);
    if (theOption.length > 0) {
        activatedValue.value = theOption[0];
    }
};
const deactivateLink = (event) => {
    event.detail.link.classList.remove('active', `bg-${props.colors.ui}`, `text-${props.colors.uiTxt}`);
};

const scrollOffset = (target) => {
    let offset = rootEl.value.$el.getBoundingClientRect().height * -1;
    const theEl = document.querySelector(target);
    if (theEl && theEl.getBoundingClientRect().top < lenis.value.scroll) {
        const nav = document.querySelector('.Navigation');
        if (nav) {
            const navRect = nav.getBoundingClientRect();
            offset -= navRect.height;
        }
    }
    return offset;
};

const calculateOverflow = () => {
    overflowing.value = false;

    nextTick(() => {
        let width = 0;
        Object.keys(anchorLinks.value).forEach((anchorKey) => {
            width += anchorLinks.value[anchorKey].getBoundingClientRect().width + 16;
        });

        overflowing.value = width >= window.outerWidth - 32;
    });
};

const scrollTo = (selectorOrElement, onComplete) => {
    lenis.value.scrollTo(selectorOrElement, {
        offset: scrollOffset(selectorOrElement),
        onComplete,
    });
};

const scrollToHref = (href) => {
    scrollTo(href, () => {
        router.push({ hash: href });
    });
};

const scrollToTarget = (target) => {
    const href = target.getAttribute('href');
    scrollToHref(href);
};

onMounted(() => {
    nextTick(() => {
        const barLinks = document.querySelectorAll(`#bar-${props.uniqueId} .AnchorBar__link`);

        // Object.keys(anchorLinks.value).forEach((anchorKey) => {
        //     sections.value.push(document.querySelector(anchorLinks.value[anchorKey].getAttribute('href')));
        // });

        if (barLinks.length > 0) {
            spy.value = new Gumshoe(`#bar-${props.uniqueId} .AnchorBar__link`, {
                offset: () => rootEl.value.$el.getBoundingClientRect().height + 100,
                reflow: true,
                events: true,
            });

            document.addEventListener('gumshoeActivate', activateLink, false);
            document.addEventListener('gumshoeDeactivate', deactivateLink, false);

            calculateOverflow();

            if (route.hash) {
                const hashElement = document.querySelector(route.hash);
                if (hashElement) {
                    scrollTo(route.hash, () => {
                        router.push({ hash: route.hash });
                    });
                }
            }

            ['resize', 'orientationchange'].forEach((ev) => {
                window.addEventListener(ev, calculateOverflow);
            });
        }
    });
});

onBeforeUnmount(() => {
    if (spy.value) {
        spy.value.destroy();
        document.removeEventListener('gumshoeActivate', activateLink);
        document.removeEventListener('gumshoeDeactivate', deactivateLink);
    }
});
</script>

<template>
    <StickyBar
        v-if="anchors.length > 0"
        :id="`bar-${uniqueId}`"
        ref="rootEl"
        :colors="colors"
    >
        <ul
            ef="barList"
            class="relative flex list-none gap-5 overflow-auto"
            :class="{ hidden: overflowing }"
        >
            <li
                v-for="anchor in anchors"
                :key="anchor"
            >
                <a
                    :ref="
                        (el) => {
                            if (el) anchorLinks[kebabCase(anchor)] = el;
                        }
                    "
                    :class="['hover:bg-' + colors.ui, 'hover:text-' + colors.uiTxt, 'text-' + colors.txt]"
                    class="AnchorBar__link inline-block whitespace-nowrap rounded-xl px-2 py-1 font-bold transition-colors duration-500 sm:px-6 sm:py-2"
                    :href="`#${kebabCase(anchor)}`"
                    v-on:click.prevent="scrollToTarget($event.target)"
                >
                    {{ anchor }}
                </a>
            </li>
        </ul>

        <CustomSelect
            :options="options"
            class="select select-anchor-type relative z-10 mr-2.5 w-[48%] md:w-auto"
            :class="{
                hidden: !overflowing,
            }"
            :colors="{ bg: colors.ui, text: colors.uiTxt }"
            :can-clear="false"
            :default-value="activatedValue"
            v-on:input="scrollToHref(`#${kebabCase($event.title)}`)"
        />
    </StickyBar>
</template>
